/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import getConfig from 'next/config';

import { HttpRoutes } from 'utils/constant';

import * as Utils from '../utils';
import { isBlankString } from '../utils';

import CommonService from './commonService';
const {
  publicRuntimeConfig: { HttpBaseUrls },
} = getConfig();

const CommonConstants = {
  MAX_MAJOR_LENGTH: 91,
  MAX_COMPANY_LENGTH: 80,
  MAX_TITLE_LENGTH: 78,
  MAX_LOCATION_LENGTH: 45,
  MAX_COLLEGE_LENGTH: 91,
  MAX_SKILL_LENGTH: 45,
  MAX_CERTIFICATIONS_LENGTH: 90,
  AUTOCOMPLETE_DEBOUNCE_MS: 50,
  MAX_LANGUAGE_LENGTH: 35,
};

function generateParams(options) {
  let params = '';

  if (options.isIndexableOnly) {
    params += '&indexableOnly=true';
  }
  if (options.stateOnlyLocations !== options.cityOnlyLocations) {
    // Both should not be same like city = true and state also true or both false, then we exclude type param
    if (options.stateOnlyLocations) {
      params += '&type=STATE';
    }
    if (options.cityOnlyLocations) {
      params += '&type=CITY';
    }
  }

  return params;
}

const generateUrl = (api, search, params) =>
  `${HttpBaseUrls.autocomplete}${api}?searchString=${search}${params}`;

const AutosuggestProxy = {
  // ('d' -> 'c' -> 'b' -> 'a') =>  'abcd'
  GetString: (head) =>
    head ? `${AutosuggestProxy.GetString(head.previousLetter)}${head.value}` : '',

  SourceHandler: (self) => {
    const AddLetter = AutosuggestProxy.SearchForLetter(self._getMatches);
    const TriggerQuery = _debounce(() => {
      if (!self._head || self._head.query.triggered) return;
      return self._head.query.trigger();
    }, CommonConstants.AUTOCOMPLETE_DEBOUNCE_MS);

    return (queryString) => {
      if (typeof queryString !== 'string' || isBlankString(queryString)) {
        self._head = null;
        return self._getSuggestionsForEmptyInput();
      }

      queryString = queryString.trim();

      const lastMatchedLetter = AutosuggestProxy.GetLastMatchedLetter(self._head, queryString);
      const matchedLetterCount = lastMatchedLetter ? lastMatchedLetter.index + 1 : 0;

      if (matchedLetterCount !== queryString.length) {
        const unmatchedLetters = queryString.substr(matchedLetterCount);
        self._head = Array.from(unmatchedLetters).reduce(
          (head, letter) => AddLetter(head, letter),
          lastMatchedLetter
        );
      } else {
        self._head = lastMatchedLetter;
      }

      TriggerQuery();

      return self._head.query.promise.catch(() => []).then((matches) => matches);
    };
  },

  InList: (self) => (selection) => {
    if (!self._head) {
      return self
        ._getSuggestionsForEmptyInput()
        .some((suggestion) => suggestion.display_name === selection.display_name);
    }
    if (!self._head.query.result) return false;
    return self._head.query.result.some(
      (suggestion) => suggestion.display_name === selection.display_name
    );
  },

  FirstSuggestion: (self) => () => {
    if (!self._head) return self._getSuggestionsForEmptyInput()[0];
    if (!self._head.query.result) return null;
    if (self._head.query.result.length === 0) return null;
    return self._head.query.result[0];
  },

  NoSuggestion: (self) => () => {
    if (!self._head) return self._getSuggestionsForEmptyInput().length === 0;
    if (!self._head.query.result) return false;
    return self._head.query.result.length === 0;
  },

  SearchForLetter: (getMatches) =>
    AutosuggestProxy.AddLetter((head, index, letter) => {
      const str = AutosuggestProxy.GetString(head) + letter;
      return AutosuggestProxy.LetterEntry(
        head,
        index,
        letter,
        AutosuggestProxy.Query(() => getMatches(str))
      );
    }),

  // queryFunc: () => Promise<[]>, the function that calls auto suggest API and returns suggestions
  /*  returns:
{
    promise: Promise<[]>, the promise that resolves to the suggestion list,
    triggered: boolean, shows if the request is already started,
    trigger: () => void, triggers request.
    result: [] | undefined, the result of query, is undefined until request completes
}
*/
  Query: (queryFunc) => {
    const self = {};
    self.promise = new Promise((resolve) => (self._resolve = resolve));
    self.trigger = () => {
      self.triggered = true;
      queryFunc().then((result) => {
        self._resolve(result);
        self.result = result;
      });
    };
    self.triggered = false;
    return self;
  },

  // createLetter: (previousLetter: LetterEntry, index: number, letter: string) => LetterEntry
  // previousLetter: LetterEntry
  // letter: string
  // If any of prefix letters is known to be no matched suggestions, will create a LetterEntry with no matched suggestions, otherwise create a new LetterEntry with 'createLetter' function
  AddLetter: (createLetter) => (previousLetter, letter) => {
    const HasNoMatchPrefix = (head) => {
      if (!head) {
        return false;
      }
      if (head.query.result && head.query.result.length === 0) return true;
      return HasNoMatchPrefix(head.previousLetter);
    };
    if (HasNoMatchPrefix(previousLetter)) {
      return AutosuggestProxy.LetterEntry(
        previousLetter,
        previousLetter.index + 1,
        letter,
        AutosuggestProxy.Query(() => Promise.resolve([]))
      );
    }
    const indexOfPreviousLetter = previousLetter ? previousLetter.index : undefined;
    const index = indexOfPreviousLetter === undefined ? 0 : indexOfPreviousLetter + 1;

    return createLetter(previousLetter, index, letter);
  },

  // previousLetter: another LetterEntry
  // index: number
  // value: string, the letter
  // query: Query
  LetterEntry: (previousLetter, index, value, query) => ({
    previousLetter,
    index,
    value,
    query,
  }),

  // (('d' -> 'c' -> 'b' -> 'a'), 'ab') => ('b' -> 'a')
  GetLastMatchedLetter: (head, queryString) => {
    if (!head) {
      return undefined;
    }
    const cachedStringLength = head.index + 1;
    const queryStringLength = queryString.length;

    const startNode =
      cachedStringLength > queryStringLength
        ? AutosuggestProxy.TrimTail(head, cachedStringLength - queryStringLength)
        : head;

    const startString =
      queryStringLength > cachedStringLength
        ? queryString.substr(0, cachedStringLength)
        : queryString;

    const Inner = (head, str, matchStartLetter) => {
      if (head === undefined && isBlankString(str)) return matchStartLetter;

      const doesCurrentLetterMatch = head.value === str[str.length - 1];

      return doesCurrentLetterMatch
        ? Inner(head.previousLetter, str.substr(0, str.length - 1), matchStartLetter || head)
        : Inner(head.previousLetter, str.substr(0, str.length - 1), undefined);
    };
    return Inner(startNode, startString, undefined);
  },

  // (('d' -> 'c' -> 'b' -> 'a'), 2) => ('b' -> 'a')
  TrimTail: (letters, count) =>
    count === 0 ? letters : AutosuggestProxy.TrimTail(letters.previousLetter, count - 1),

  alternativeSuggest: (payload) => {
    const payloadBck = payload;
    const otherResults = [];

    if (!_isEmpty(payload)) {
      return new Promise((resolve, reject) => {
        CommonService.normalizedData(payload)
          .then((response) => {
            if (!response.data) return resolve(otherResults);
            if (
              (!payloadBck.organization || payloadBck.organization.length === 0) &&
              response.data.closest_lay_title &&
              response.data.closest_lay_title !== 'N/A'
            ) {
              const Object = {
                original_name: response.data.closest_lay_title,
                display_name: response.data.closest_lay_title,
                value: response.data.closest_lay_title,
              };
              otherResults.push(Object);
            } else if (response.data.major && response.data.major !== 'N/A') {
              const Object = {
                display_name: response.data.major,
                name: response.data.major,
                value: response.data.major,
                original_name: response.data.major,
              };
              otherResults.push(Object);
            } else if (payloadBck.organization && response.data.normalizedCompanyName) {
              const Object = {
                companyName: response.data.normalizedCompanyName,
                companyID: response.data.companyID,
                key: response.data.normalizedCompanyName,
              };
              otherResults.push(Object);
            }
            resolve(otherResults);
          })
          .catch((err) => {
            reject(err);
          });
      });
    } else {
      return Promise.resolve(otherResults);
    }
  },

  // getMatches: (searchString: string) => Promise<SuggestionItem[]> , SuggestionItem usually looks like { display_name: string, ... }. The function that call auto suggestion API and returns a list of suggestions.
  // getSuggestionsForEmptyInput: null | () => SuggestionItem[]. When the input string is empty, will suggest the array returned from this function. If this function is not provided, use empty array instead.
  state: (getMatches, getSuggestionsForEmptyInput) => {
    const self = {};

    // LetterEntry, point to the last input letter.
    // Represents the current input state. For example, if current input is 'abcd', _head will be ('d' -> 'c' -> 'b' -> 'a')
    // In case there are no input (empty string), _head will be null/undefined
    self._head = undefined;
    self._getMatches = getMatches;
    self._getSuggestionsForEmptyInput = getSuggestionsForEmptyInput || (() => Promise.resolve([]));

    // () => string: last Input string
    self.lastInput = () => AutosuggestProxy.GetString(self._head);

    // (queryString) => array: For typeahead
    self.sourceHandler = AutosuggestProxy.SourceHandler(self);

    // {display_name: string, ...} => Boolean: Is giving item included in the LAST suggestion list.
    // If the API call for last input is not completed, the function always returns false.
    self.suggests = AutosuggestProxy.InList(self);

    // () => { display_name: string, ... } | null
    // Get the first suggestion for the last input
    // Null if no matches/ API call not yet completed
    self.firstSuggestion = AutosuggestProxy.FirstSuggestion(self);

    // () => boolean
    // true, if API call for last call completed and no matches found
    // false, if API call not completed or any results found
    self.noSuggestion = AutosuggestProxy.NoSuggestion(self);

    // () => Promise<Suggestion[]>
    // Returns a Promise that resolves to a list of suggestions for CURRENT input
    self.lastQuery = () => {
      if (!self._head) {
        return Promise.resolve([]);
      }

      if (!self._head.query.triggered) self._head.query.trigger();
      return self._head.query.promise;
    };
    return self;
  },

  forTitle: (getSuggestionsForEmptyInput, options = {}) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_TITLE_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestTitle(queryString, generateParams(options)).then(
        (data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => {
            const name = item.name || item.display_name;
            return {
              ...item,
              name:
                item.original_name &&
                name.toLowerCase() !== item.original_name.toLowerCase() &&
                name.indexOf(`(${item.original_name})`) === -1
                  ? `${name} (${item.original_name})`
                  : name,
            };
          });
        }
      );
    }, getSuggestionsForEmptyInput),

  forLocation: (getSuggestionsForEmptyInput, options) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_LOCATION_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestLocation(queryString, generateParams(options)).then(
        (data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => {
            const location = Utils.splitLocation(item.display_name);
            return {
              ...item,
              value: {
                city: location.city,
                state: location.stateAbbr,
              },
            };
          });
        }
      );
    }, getSuggestionsForEmptyInput),

  forLocationExtended: (getSuggestionsForEmptyInput) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_LOCATION_LENGTH) {
        return Promise.resolve([]);
      }
      // eslint-disable-next-line no-param-reassign
      return AutosuggestProxy.getAutosuggestLocation(queryString, '&extended=true').then((data) => {
        if (!data || !Array.isArray(data)) {
          return [];
        }
        return data.map((item) => {
          if (item.extended) {
            // eslint-disable-next-line no-param-reassign
            return {
              ...item,
              value: {
                city: '',
                state: '',
              },
            };
          }
          const location = Utils.splitLocation(item.display_name);
          return {
            ...item,
            value: {
              city: location.city,
              state: location.stateAbbr,
            },
          };
        });
      });
    }, getSuggestionsForEmptyInput),

  forMajor: (getSuggestionsForEmptyInput, options) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_MAJOR_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestMajor(queryString, generateParams(options)).then(
        (data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => ({
            ...item,
            display_name:
              item.name.toLowerCase() !== item.original_name.toLowerCase() &&
              item.display_name.indexOf(`(${item.original_name})`) === -1
                ? `${item.display_name} (${item.original_name})`
                : item.display_name,
            value: item,
          }));
        }
      );
    }, getSuggestionsForEmptyInput),

  forCompany: (getSuggestionsForEmptyInput, options) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_COMPANY_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestCompany(encodeURIComponent(queryString), generateParams(options)).then(
        (data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => ({
            ...item,
            display_name: item.companyName,
            name: item.companyID,
            value: item,
          }));
        }
      );
    }, getSuggestionsForEmptyInput),

  forCollege: (getSuggestionsForEmptyInput) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_COLLEGE_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestCollege(queryString).then((data) => {
        if (!data || !Array.isArray(data)) {
          return [];
        }
        console.log({data});
        return data.map((item) => ({
          ...item,
          display_name:
            item.name.toLowerCase() !== item.name.toLowerCase() &&
            item.display_name.indexOf(`(${item.name})`) === -1
              ? `${item.display_name} (${item.name})`
              : item.display_name,
          value: item,
        }));
      });
    }, getSuggestionsForEmptyInput),

  forSkills: (getSuggestionsForEmptyInput) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_SKILL_LENGTH) {
        return Promise.resolve([]);
      }

      return AutosuggestProxy.getAutosuggestSkills(queryString).then((data) =>
        data.map((item) => ({
          ...item,
          value: item,
          display_name: item.name,
        }))
      );
    }, getSuggestionsForEmptyInput),

  forCertifications: (getSuggestionsForEmptyInput) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_CERTIFICATIONS_LENGTH) {
        return Promise.resolve([]);
      }

      return AutosuggestProxy.getAutosuggestCertifications(queryString).then((data) =>
        data.map((item) => ({
          ...item,
          value: item,
          display_name: item.name,
        }))
      );
    }, getSuggestionsForEmptyInput),

  forLanguage: (getSuggestionsForEmptyInput) =>
    AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_LANGUAGE_LENGTH) {
        return Promise.resolve([]);
      }

      return AutosuggestProxy.getAutosuggestLanguage(queryString).then((data) =>
        data.map((item) => ({
          ...item,
          value: item,
          display_name: item.name,
        }))
      );
    }, getSuggestionsForEmptyInput),

  getAutosuggestTitle: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    if (
      window.__NEXT_REDUX_STORE__ &&
      window.__NEXT_REDUX_STORE__.getState &&
      window.__NEXT_REDUX_STORE__.getState()['searchFilter']['searchForParticularDepartment']
    ) {
      const dataSource =
        window.particularDepartments[
          window.__NEXT_REDUX_STORE__.getState()['searchFilter']['searchWithoutCallNumber']
        ]['salaries'];
      const results = [];
      stringSearch = stringSearch
        .replace('.', '\\.')
        .replace('(', '\\(')
        .replace('*', '\\*')
        .replace('?', '\\?')
        .replace('+', '\\+')
        .replace('[', '\\[')
        .replace(']', '\\]')
        .replace('{', '\\{')
        .replace('}', '\\}')
        .replace('^', '\\^')
        .replace('$', '\\$')
        .replace('|', '\\|')
        .replace('\\', '\\\\')
        .replace('/', '\\/');
      const reg = new RegExp(stringSearch, 'gi');
      for (let value of dataSource) {
        if (value.jobTitle.match(reg)) {
          results.push({
            display_name: value.jobTitle,
            name: value.jobTitle,
            original_name: value.jobTitle,
            value: {
              display_name: value.jobTitle,
              name: value.jobTitle,
              original_name: value.jobTitle,
            },
          });
        }
      }
      return Promise.resolve(results);
    } else {
      stringSearch = stringSearch.replace('&', '');
      const url = `${HttpBaseUrls.autocomplete + HttpRoutes.autocompleteJobTitle}?searchString=${
        stringSearch + queryParams
      }`;
      return axios
        .get(url)
        .then((response) => {
          if ((!response.data || response.data.length < 1) && !parameters?.includes("indexableOnly=true")) {
            const payload = { title: stringSearch };
            return AutosuggestProxy.alternativeSuggest(payload).then((result) => {
              return result;
            });
          }
          response.data = response.data.map((item) => ({
            ...item,
            display_name: item.name,
            original_name: item.original_name,
            value: item,
          }));
          return response.data;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    }
  },

  getAutosuggestLocation: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpBaseUrls.autocomplete + HttpRoutes.autocompleteLocation}?searchString=${
      stringSearch + queryParams
    }`;
    return axios
      .get(url)
      .then((response) => {
        if (!response.data || response.data.length < 1) {
          const payload = { location: stringSearch };
          return AutosuggestProxy.alternativeSuggest(payload).then((result) => result);
        }
        return response.data;
      })
      .catch((error) => error);
  },

  getAutosuggestMajor: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpBaseUrls.autocomplete + HttpRoutes.autocompleteMajor}?searchString=${
      stringSearch + queryParams
    }`;

    return axios
      .get(url)
      .then((response) => {
        if (!response.data || response.data.length < 1) {
          const payload = { major: stringSearch };
          return AutosuggestProxy.alternativeSuggest(payload).then((result) => result);
        }
        return response.data;
      })
      .catch((error) => error);
  },

  getAutosuggestCompany: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpBaseUrls.autocomplete + HttpRoutes.autocompleteCompanyName}?searchString=${
      stringSearch + queryParams
    }`;

    return axios
      .get(url)
      .then((response) => response.data)
      .catch((error) => console.error(error));
  },

  getAutosuggestCollege: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpBaseUrls.autocomplete + HttpRoutes.autocompleteCollege}?searchString=${
      stringSearch + queryParams
    }`;

    return axios
      .get(url)
      .then((response) => response?.data ?? [])
      .catch((error) => error);
  },

  getAutosuggestSkills: (search, params = '') => {
    const url = `${HttpBaseUrls.autocomplete}${HttpRoutes.autocompleteSkills}?searchString=${search}${params}`;

    return axios
      .get(url)
      .then((response) => {
        if (response?.data?.length) {
          return response.data;
        }

        return [];
      })
      .catch((error) => error);
  },

  getAutosuggestCertifications: (search, params = '') => {
    const url = `${HttpBaseUrls.autocomplete}${HttpRoutes.autocompleteCertifications}?searchString=${search}${params}`;

    return axios
      .get(url)
      .then((response) => {
        if (response?.data?.length) {
          return response.data;
        }

        return [];
      })
      .catch((error) => error);
  },

  getAutosuggestLanguage: (search, params = '') =>
    axios
      .get(generateUrl(HttpRoutes.autocompleteLanguage, search, params))
      .then((response) => {
        if (response?.data?.length) {
          return response.data;
        }

        return [];
      })
      .catch((error) => error),
};

export default AutosuggestProxy;
